import React, { useState, useEffect } from "react";
import { signOut } from "firebase/auth";
import { getFirestore, doc, setDoc, collection, addDoc, query, where, getDocs, deleteDoc, getDoc, onSnapshot } from "firebase/firestore";
import app, { deanAuth, useFirebaseEmulators } from "../../firebase";
import ResetPassword from "../admin/users/ResetPassword";
import { useNavigate } from "react-router-dom";
import { isCalendarDateInRange } from "../../utils/announcementDates";
import { requestAccountDeletion } from "../../services/accountDeletion";
import { useAccountDeletionRecovery } from "../../hooks/useAccountDeletionRecovery";
import { createManagedAccount } from "../../services/studentAccountCreation";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

const auth = deanAuth;
const db = getFirestore(app);
const generateProgramHeadPassword = () => {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const symbols = "!@";
  const all = upper + lower + digits + symbols;
  const makeRandomValues = count => {
    const values = new Uint32Array(count);
    if (window?.crypto?.getRandomValues) {
      window.crypto.getRandomValues(values);
    } else {
      for (let i = 0; i < count; i += 1) {
        values[i] = Math.floor(Math.random() * 0xffffffff);
      }
    }
    return values;
  };
  const values = makeRandomValues(19);
  const pick = (source, index) => source[values[index] % source.length];
  const password = [
    pick(upper, 0),
    pick(lower, 1),
    pick(digits, 2),
    pick(symbols, 3),
    ...Array.from({ length: 6 }, (_, index) => pick(all, index + 4))
  ];

  for (let i = password.length - 1; i > 0; i -= 1) {
    const j = values[i + 9] % (i + 1);
    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join("");
};
const toNonEmptyStringArray = (value) =>
  (Array.isArray(value) ? value : value ? [value] : []).filter(
    item => typeof item === "string" && item.trim()
  );
const toValidDate = (value) => {
  if (!value) return null;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

function DeanDashboard() {
  const navigate = useNavigate();
  const {
    pendingDeletions: pendingRecoveryItems,
    recoveryMessage,
    retryPendingDeletions
  } = useAccountDeletionRecovery(auth.currentUser);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Navigation state
  const [page, setPage] = useState("menu"); // "menu", "department", "programHead", "professor"

  // State
  const [school, setSchool] = useState("");
  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  const [schoolsError, setSchoolsError] = useState("");

  // Department
  const [departmentName, setDepartmentName] = useState("");
  const [deptMsg, setDeptMsg] = useState("");
  const [deptSaving, setDeptSaving] = useState(false);
  const [deptNameFilter, setDeptNameFilter] = useState("");
  const [departments, setDepartments] = useState([]);
  const [editModal, setEditModal] = useState(null);
  const [editModalError, setEditModalError] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [professorEditCourses, setProfessorEditCourses] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [pendingDeletionOperationId, setPendingDeletionOperationId] = useState("");
  const [deletionRecoveryMessage, setDeletionRecoveryMessage] = useState("");

  // Program Head
  const [phEmail, setPhEmail] = useState("");
  const [phPassword, setPhPassword] = useState(generateProgramHeadPassword);
  const [phShowPassword, setPhShowPassword] = useState(false);
  const [phSaving, setPhSaving] = useState(false);
  const [phSaveStage, setPhSaveStage] = useState("");
  const [phPendingCreation, setPhPendingCreation] = useState(null);
  const [phName, setPhName] = useState("");
  const [phDepartment, setPhDepartment] = useState("");
  const [phDepartmentDropdownOpen, setPhDepartmentDropdownOpen] = useState(false);
  const [phMsg, setPhMsg] = useState("");
  const [programHeads, setProgramHeads] = useState([]);
  const [phNameFilter, setPhNameFilter] = useState("");
  const [phDepartmentFilter, setPhDepartmentFilter] = useState("");

  // Professor
  const [profEmail, setProfEmail] = useState("");
  const [profPassword, setProfPassword] = useState(generateProgramHeadPassword);
  const [profShowPassword, setProfShowPassword] = useState(false);
  const [profSaving, setProfSaving] = useState(false);
  const [profSaveStage, setProfSaveStage] = useState("");
  const [profPendingCreation, setProfPendingCreation] = useState(null);
  const [profName, setProfName] = useState("");
  const [profDepartments, setProfDepartments] = useState([]);
  const [profCourses, setProfCourses] = useState([]);
  const [profMsg, setProfMsg] = useState("");
  const [professors, setProfessors] = useState([]);
  const [courseList, setCourseList] = useState([]);
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const [coursesDropdownOpen, setCoursesDropdownOpen] = useState(false);
  const [profNameFilter, setProfNameFilter] = useState("");
  const [profDepartmentFilter, setProfDepartmentFilter] = useState("");
  const [profSubjectFilter, setProfSubjectFilter] = useState("");
  const [profSubjectDropdownOpen, setProfSubjectDropdownOpen] = useState(false);

  useEffect(() => {
    if (!profMsg) return;
    const timeoutId = window.setTimeout(() => setProfMsg(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [profMsg]);

  // Dean Info
  const [deanName, setDeanName] = useState("");
  const [deanSchool, setDeanSchool] = useState("");
  const [deanEmail, setDeanEmail] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [dashboardStudents, setDashboardStudents] = useState([]);
  const [dashboardStudentsLoading, setDashboardStudentsLoading] = useState(true);
  const [dashboardDeanCount, setDashboardDeanCount] = useState(0);
  const [dashboardNow, setDashboardNow] = useState(() => new Date());
  const [academicCalendar, setAcademicCalendar] = useState(null);

  // Add new state for names
  const [phFirstName, setPhFirstName] = useState("");
  const [phMiddleName, setPhMiddleName] = useState("");
  const [phLastName, setPhLastName] = useState("");
  const [profFirstName, setProfFirstName] = useState("");
  const [profMiddleName, setProfMiddleName] = useState("");
  const [profLastName, setProfLastName] = useState("");

  // Pagination states
  const [deptTablePage, setDeptTablePage] = useState(1);
  const [phTablePage, setPhTablePage] = useState(1);
  const [profTablePage, setProfTablePage] = useState(1);
  const itemsPerPage = 15;
  const departmentFilterOptions = [...new Set(departments
    .map(department => typeof department.name === "string" ? department.name.trim() : "")
    .filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const filteredDepartments = departments.filter(department =>
    !deptNameFilter || department.name === deptNameFilter
  );
  const professorDepartmentOptions = [...new Set(professors.flatMap(prof => [
    ...toNonEmptyStringArray(prof.department),
    ...toNonEmptyStringArray(prof.departments)
  ]))].sort((a, b) => a.localeCompare(b));
  const professorSubjectOptions = [...new Set(professors.flatMap(prof =>
    toNonEmptyStringArray(prof.courses)
  ))].sort((a, b) => a.localeCompare(b));
  const programHeadDepartmentOptions = [...new Set(programHeads
    .map(programHead => typeof programHead.department === "string" ? programHead.department.trim() : "")
    .filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const filteredProgramHeads = programHeads.filter(programHead => {
    const name = typeof programHead.name === "string"
      ? programHead.name
      : [programHead.firstName, programHead.middleName, programHead.lastName].filter(Boolean).join(" ");
    return name.toLowerCase().includes(phNameFilter.trim().toLowerCase()) &&
      (!phDepartmentFilter || programHead.department === phDepartmentFilter);
  });
  const filteredProfessors = professors.filter(prof => {
    const name = typeof prof.name === "string"
      ? prof.name
      : [prof.firstName, prof.middleName, prof.lastName].filter(Boolean).join(" ");
    const assignedDepartments = [
      ...toNonEmptyStringArray(prof.department),
      ...toNonEmptyStringArray(prof.departments)
    ];
    const assignedSubjects = toNonEmptyStringArray(prof.courses);

    return name.toLowerCase().includes(profNameFilter.trim().toLowerCase()) &&
      (!profDepartmentFilter || assignedDepartments.includes(profDepartmentFilter)) &&
      (!profSubjectFilter || assignedSubjects.includes(profSubjectFilter));
  });
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredDepartments.length / itemsPerPage));
    setDeptTablePage(currentPage => Math.min(currentPage, totalPages));
  }, [filteredDepartments.length, itemsPerPage]);
  const schoolDepartmentNames = [...new Set(departments
    .map(department => department.name)
    .filter(name => typeof name === "string" && name.trim()))];
  const schoolStudents = dashboardStudents.filter(student =>
    schoolDepartmentNames.includes(student.program || student.department)
  );
  const currentMonthStart = new Date(dashboardNow.getFullYear(), dashboardNow.getMonth(), 1);
  const nextMonthStart = new Date(dashboardNow.getFullYear(), dashboardNow.getMonth() + 1, 1);
  const previousMonthStart = new Date(dashboardNow.getFullYear(), dashboardNow.getMonth() - 1, 1);
  const isDateInRange = (value, start, end) => {
    const date = toValidDate(value);
    return date && date >= start && date < end;
  };
  const newEnrollees = schoolStudents.filter(student =>
    isDateInRange(student.enrolledDate, currentMonthStart, nextMonthStart)
  ).length;
  const pendingEnrollment = schoolStudents.filter(student =>
    String(student.registrationStatus || "").toLowerCase() === "submitted"
  ).length;
  const previousMonthEnrollees = schoolStudents.filter(student =>
    isDateInRange(student.enrolledDate, previousMonthStart, currentMonthStart)
  ).length;
  const enrollmentGrowth = previousMonthEnrollees === 0
    ? (newEnrollees > 0 ? null : 0)
    : ((newEnrollees - previousMonthEnrollees) / previousMonthEnrollees) * 100;
  const formatEnrollmentGrowth = growth =>
    growth === null ? "No previous-month" : `${growth > 0 ? "+" : ""}${growth.toFixed(1)}%`;
  const activeAcademicTerm = [
    {
      term: "1st Semester",
      schoolYearStart: academicCalendar?.firstSemYearStart,
      schoolYearEnd: academicCalendar?.firstSemYearEnd,
      start: academicCalendar?.firstSemStart,
      end: academicCalendar?.firstSemEnd
    },
    {
      term: "2nd Semester",
      schoolYearStart: academicCalendar?.secondSemYearStart,
      schoolYearEnd: academicCalendar?.secondSemYearEnd,
      start: academicCalendar?.secondSemStart,
      end: academicCalendar?.secondSemEnd
    }
  ].find(semester => {
    return isCalendarDateInRange(semester.start, semester.end, dashboardNow);
  });
  const academicTermIndicator = activeAcademicTerm
    ? `${activeAcademicTerm.schoolYearStart && activeAcademicTerm.schoolYearEnd
      ? `AY ${activeAcademicTerm.schoolYearStart}-${activeAcademicTerm.schoolYearEnd}`
      : "School year not configured"} · ${activeAcademicTerm.term}`
    : "No active term";
  const departmentEnrollmentStats = schoolDepartmentNames.map(department => {
    const departmentStudents = schoolStudents.filter(student =>
      (student.program || student.department) === department
    );
    const currentMonthCount = departmentStudents.filter(student =>
      isDateInRange(student.enrolledDate, currentMonthStart, nextMonthStart)
    ).length;
    const previousMonthCount = departmentStudents.filter(student =>
      isDateInRange(student.enrolledDate, previousMonthStart, currentMonthStart)
    ).length;
    return {
      department,
      totalStudents: departmentStudents.length,
      newEnrollees: currentMonthCount,
      pendingEnrollment: departmentStudents.filter(student =>
        String(student.registrationStatus || "").toLowerCase() === "submitted"
      ).length,
      enrollmentGrowth: previousMonthCount === 0
        ? (currentMonthCount > 0 ? null : 0)
        : ((currentMonthCount - previousMonthCount) / previousMonthCount) * 100
    };
  }).sort((a, b) => a.department.localeCompare(b.department));

  // Fetch all schools for dropdown
  useEffect(() => {
    const fetchSchools = async () => {
      setSchoolsLoading(true);
      setSchoolsError("");
      try {
        const schoolsSnapshot = await getDocs(collection(db, "schools"));
        const schoolNames = schoolsSnapshot.docs.map(doc => doc.data().name);
        setSchools(schoolNames);
      } catch (err) {
        setSchoolsError("Failed to load schools.");
      }
      setSchoolsLoading(false);
    };
    fetchSchools();
  }, []);

  // Fetch dean info
  useEffect(() => {
    const fetchDeanInfo = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const deanFullName = [data.firstName, data.lastName].filter(Boolean).join(" ");
          setDeanName(deanFullName);
          setDeanSchool(data.school || "");
          setDeanEmail(data.email || user.email || "");
        }
      } catch (err) {
        console.error("Failed to load Dean profile", err);
      }
    };
    fetchDeanInfo();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => setDashboardNow(new Date()), 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "systemSettings", "announcement"), snapshot => {
      setAcademicCalendar(snapshot.exists() ? snapshot.data() : null);
    }, error => {
      console.error("Error loading current academic term:", error);
      setAcademicCalendar(null);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!deanSchool) {
      setDashboardStudents([]);
      setDashboardStudentsLoading(false);
      return;
    }
    setDashboardStudentsLoading(true);
    const unsubscribe = onSnapshot(collection(db, "students"), snapshot => {
      setDashboardStudents(snapshot.docs.map(studentDoc => ({ id: studentDoc.id, ...studentDoc.data() })));
      setDashboardStudentsLoading(false);
    }, error => {
      console.error("Error loading Dean dashboard students:", error);
      setDashboardStudents([]);
      setDashboardStudentsLoading(false);
    });
    return unsubscribe;
  }, [deanSchool]);

  useEffect(() => {
    if (!deanSchool) {
      setDashboardDeanCount(0);
      return;
    }
    const deanQuery = query(
      collection(db, "users"),
      where("role", "==", "Dean"),
      where("school", "==", deanSchool)
    );
    const unsubscribe = onSnapshot(deanQuery, snapshot => {
      setDashboardDeanCount(snapshot.size);
    }, error => {
      console.error("Error loading Dean count:", error);
      setDashboardDeanCount(0);
    });
    return unsubscribe;
  }, [deanSchool]);

  // Fetch all departments for this dean (filter by school if needed) - Real-time listener
  useEffect(() => {
    if (deanSchool === "General Education") {
      // For GE dean, fetch unique departments from curriculum_subjects with minor subjects
      const fetchGEDepartments = async () => {
        try {
          const q = query(
            collection(db, "curriculum_subjects"),
            where("isMinorSubject", "==", true)
          );
          const snap = await getDocs(q);
          const uniqueDepts = new Set();
          snap.docs.forEach(doc => {
            if (doc.data().department) {
              uniqueDepts.add(doc.data().department);
            }
          });
          setDepartments(
            Array.from(uniqueDepts).map((dept, idx) => ({ id: dept, name: dept }))
          );
        } catch (err) {
          console.error("Error fetching GE departments:", err);
          setDepartments([]);
        }
      };
      fetchGEDepartments();
      return;
    }
    if (!deanSchool) return;

    const q = query(
      collection(db, "departments"),
      where("school", "==", deanSchool)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setDepartments(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error("Error fetching departments:", err);
      setDepartments([]);
    });
    return unsubscribe;
  }, [deanSchool]);

  // Fetch program heads for this dean - Real-time listener
  useEffect(() => {
    if (!deanSchool) {
      setProgramHeads([]);
      return;
    }

    let q;
    if (deanSchool === "General Education") {
      q = query(
        collection(db, "users"),
        where("role", "==", "Program Head"),
        where("department", "==", "General Education")
      );
    } else {
      q = query(
        collection(db, "users"),
        where("role", "==", "Program Head"),
        where("school", "==", deanSchool)
      );
    }
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setProgramHeads(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error("Error fetching program heads:", err);
      setProgramHeads([]);
    });
    return unsubscribe;
  }, [deanSchool]);

  // Fetch professors for this dean (always use deanSchool) - Real-time listener
  useEffect(() => {
    if (!deanSchool) {
      setProfessors([]);
      return;
    }

    const q = query(
      collection(db, "users"),
      where("role", "==", "Professor"),
      where("school", "==", deanSchool)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setProfessors(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error("Error fetching professors:", err);
      setProfessors([]);
    });
    return unsubscribe;
  }, [deanSchool]);

  // Fetch course names for selected department (for Add Professor)
  useEffect(() => {
    const fetchCourses = async () => {
      if (profDepartments.length === 0) {
        setCourseList([]);
        return;
      }

      const departmentValues = profDepartments.slice(0, 10);
      const subjQ = query(
        collection(db, "curriculum_subjects"),
        where("department", "in", departmentValues)
      );

      const subjSnap = await getDocs(subjQ);
      let courses = subjSnap.docs
        .map(doc => doc.data())
        .filter(data => {
          if (deanSchool === "General Education") {
            return data.isMinorSubject === true;
          }
          return data.isMinorSubject !== true;
        })
        .map(data => ({
          courseId: data.courseId || "",
          courseName: data.courseName || ""
        }))
        .filter(item => item.courseName.trim());

      // Remove duplicates and sort alphabetically by courseId
      const uniqueCourses = [];
      const seenNames = new Set();
      courses.forEach(course => {
        if (!seenNames.has(course.courseName)) {
          seenNames.add(course.courseName);
          uniqueCourses.push(course);
        }
      });
      uniqueCourses.sort((a, b) => (a.courseId || "").localeCompare(b.courseId || ""));

      setCourseList(uniqueCourses);
    };
    fetchCourses();
  }, [profDepartments, deanSchool]);

  const editModalType = editModal?.type;
  const editModalDepartments = editModal?.departments;

  useEffect(() => {
    let cancelled = false;
    const fetchEditCourses = async () => {
      if (editModalType !== "professor" || !editModalDepartments || editModalDepartments.length === 0) {
        setProfessorEditCourses([]);
        return;
      }
      try {
        const subjQ = query(
          collection(db, "curriculum_subjects"),
          where("department", "in", editModalDepartments.slice(0, 10))
        );
        const subjSnap = await getDocs(subjQ);
        const options = subjSnap.docs
          .map(subjectDoc => subjectDoc.data())
          .filter(data => deanSchool === "General Education" ? data.isMinorSubject === true : data.isMinorSubject !== true)
          .map(data => ({
            courseId: data.courseId || "",
            courseName: data.courseName || ""
          }))
          .filter(course => course.courseName.trim());
        const uniqueOptions = Array.from(
          new Map(options.map(course => [
            course.courseId ? `${course.courseId} - ${course.courseName}` : course.courseName,
            course
          ])).values()
        ).sort((a, b) => (a.courseId || "").localeCompare(b.courseId || ""));
        if (!cancelled) setProfessorEditCourses(uniqueOptions);
      } catch (err) {
        console.error("Error fetching professor edit courses:", err);
        if (!cancelled) setEditModalError("Unable to load courses for the selected departments.");
      }
    };
    fetchEditCourses();
    return () => {
      cancelled = true;
    };
  }, [editModalType, editModalDepartments, deanSchool]);

  const toggleProfDepartment = (departmentName) => {
    setProfDepartments(prev => {
      if (prev.includes(departmentName)) {
        return prev.filter(item => item !== departmentName);
      }
      return [...prev, departmentName];
    });
  };

  const toggleProfCourse = (courseDisplay) => {
    setProfCourses(prev => {
      if (prev.includes(courseDisplay)) {
        return prev.filter(item => item !== courseDisplay);
      }
      return [...prev, courseDisplay];
    });
  };

  // Add Department handler
  const handleAddDepartment = async (e) => {
    e.preventDefault();
    setDeptMsg("");
    if (deptSaving) return;
    if (!deanSchool) {
      setDeptMsg("Dean's school not found.");
      return;
    }
    const normalizedName = departmentName.trim();
    if (!normalizedName) {
      setDeptMsg("Department name is required.");
      return;
    }

    setDeptSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated.");
      const departmentQuery = query(
        collection(db, "departments"),
        where("school", "==", deanSchool)
      );
      const existingDepartments = await getDocs(departmentQuery);
      const duplicateExists = existingDepartments.docs.some(department =>
        typeof department.data().name === "string" &&
        department.data().name.trim().toLowerCase() === normalizedName.toLowerCase()
      );
      if (duplicateExists) {
        setDeptMsg("A department with this name already exists in your school.");
        return;
      }
      await addDoc(collection(db, "departments"), {
        name: normalizedName,
        school: deanSchool
      });
      setDeptMsg("Department added!");
      setDepartmentName("");
    } catch (err) {
      setDeptMsg(err.message);
    } finally {
      setDeptSaving(false);
    }
  };

  // Add Program Head handler
  const handleAddProgramHead = async (e) => {
    e.preventDefault();
    setPhMsg("");
    if (!deanSchool) {
      setPhMsg("Dean's school not found.");
      return;
    }
    if (deanSchool !== "General Education" && !phDepartment) {
      setPhMsg("Please select a department.");
      return;
    }
    if (phSaving) return;

    setPhSaving(true);
    setPhSaveStage(phPendingCreation ? "Saving profile..." : "Creating account...");
    try {
      let pendingCreation = phPendingCreation;
      if (!pendingCreation) {
        pendingCreation = {
          profile: {
            firstName: phFirstName,
            middleName: phMiddleName,
            lastName: phLastName,
            name: `${phFirstName} ${phMiddleName} ${phLastName}`.replace(/\s+/g, " ").trim(),
            email: phEmail,
            school: deanSchool,
            department: deanSchool === "General Education" ? "General Education" : phDepartment,
            role: "Program Head"
          },
          password: phPassword
        };
        setPhPendingCreation(pendingCreation);
      }

      setPhSaveStage("Creating account and profile...");
      await createManagedAccount({
        caller: auth.currentUser,
        email: pendingCreation.profile.email,
        displayName: pendingCreation.profile.name,
        password: pendingCreation.password,
        staffProfile: pendingCreation.profile
      });

      setPhSaveStage("Sending credentials email...");
      try {
        if (useFirebaseEmulators) {
          setPhMsg("Program Head account created in the emulator; credentials email was not sent.");
        } else {
        const response = await fetch("https://sendapprovalemail-ag2nilnpda-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: pendingCreation.profile.email,
            name: `${pendingCreation.profile.firstName || ""} ${pendingCreation.profile.lastName || ""}`.trim(),
            idNumber: "",
            program: pendingCreation.profile.role,
            password: pendingCreation.password,
            template: "userCredentials",
            loginUrl: "https://enrollease-19c30.web.app/academics"
          })
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `${response.status} ${response.statusText}`);
        }
        }
      } catch (err) {
        setPhMsg(`Program Head account and profile were created, but the credentials email could not be sent: ${err.message}`);
        return;
      }

      if (!useFirebaseEmulators) setPhMsg("Program Head added!");
      setPhEmail("");
      setPhPassword(generateProgramHeadPassword());
      setPhShowPassword(false);
      setPhPendingCreation(null);
      setPhSaveStage("");
      setPhFirstName("");
      setPhMiddleName("");
      setPhLastName("");
      setPhDepartment("");
      setPhDepartmentDropdownOpen(false);
    } catch (err) {
      setPhMsg(`Unable to complete Program Head creation: ${err.message}`);
    } finally {
      setPhSaving(false);
      setPhSaveStage("");
    }
  };

  // Add Professor handler
  const handleAddProfessor = async (e) => {
    e.preventDefault();
    setProfMsg("");
    if (profSaving) return;
    if (!deanSchool) {
      setProfMsg("Dean's school not found.");
      return;
    }
    if (profDepartments.length === 0) {
      setProfMsg("Please select at least one department.");
      return;
    }
    if (profCourses.length === 0) {
      setProfMsg("Please select at least one course.");
      return;
    }
    setProfSaving(true);
    setProfSaveStage(profPendingCreation ? "Saving profile..." : "Creating account...");
    try {
      let pendingCreation = profPendingCreation;
      if (!pendingCreation) {
        pendingCreation = {
          profile: {
            firstName: profFirstName,
            middleName: profMiddleName,
            lastName: profLastName,
            name: `${profFirstName} ${profMiddleName} ${profLastName}`.replace(/\s+/g, " ").trim(),
            email: profEmail,
            school: deanSchool,
            department: profDepartments[0] || "",
            departments: profDepartments,
            courses: profCourses,
            role: "Professor"
          },
          password: profPassword
        };
        setProfPendingCreation(pendingCreation);
      }

      setProfSaveStage("Creating account and profile...");
      await createManagedAccount({
        caller: auth.currentUser,
        email: pendingCreation.profile.email,
        displayName: pendingCreation.profile.name,
        password: pendingCreation.password,
        staffProfile: pendingCreation.profile
      });

      setProfSaveStage("Sending credentials email...");
      try {
        if (useFirebaseEmulators) {
          setProfMsg("Professor account created in the emulator; credentials email was not sent.");
        } else {
        const response = await fetch("https://sendapprovalemail-ag2nilnpda-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: pendingCreation.profile.email,
            name: `${pendingCreation.profile.firstName || ""} ${pendingCreation.profile.lastName || ""}`.trim(),
            idNumber: "",
            program: pendingCreation.profile.role,
            password: pendingCreation.password,
            template: "userCredentials",
            loginUrl: "https://enrollease-19c30.web.app/academics"
          })
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `${response.status} ${response.statusText}`);
        }
        }
      } catch (err) {
        setProfMsg(`Professor account and profile were created, but the credentials email could not be sent: ${err.message}`);
        return;
      }

      if (!useFirebaseEmulators) setProfMsg("Professor added!");
      setProfEmail("");
      setProfPassword(generateProgramHeadPassword());
      setProfShowPassword(false);
      setProfPendingCreation(null);
      setProfFirstName("");
      setProfMiddleName("");
      setProfLastName("");
      setProfDepartments([]);
      setProfCourses([]);
      setDepartmentDropdownOpen(false);
      setCoursesDropdownOpen(false);
    } catch (err) {
      setProfMsg(`Unable to complete Professor creation: ${err.message}`);
    } finally {
      setProfSaving(false);
      setProfSaveStage("");
    }
  };

  const handleDeleteDepartment = (id) => {
    const department = departments.find(item => item.id === id);
    if (department) setDeleteConfirmation({ type: "department", id, name: department.name || "Unnamed department" });
  };

  const handleDeleteProgramHead = (id) => {
    const programHead = programHeads.find(item => item.id === id);
    if (programHead) setDeleteConfirmation({ type: "programHead", id, name: programHead.name || programHead.email || "Program Head" });
  };

  const handleDeleteProfessor = (id) => {
    const professor = professors.find(item => item.id === id);
    if (professor) setDeleteConfirmation({ type: "professor", id, name: professor.name || professor.email || "Professor" });
  };

  const handleEditDepartment = (id) => {
    const department = departments.find(item => item.id === id);
    if (!department) {
      setDeptMsg("Department not found.");
      return;
    }
    setEditModal({ type: "department", id, name: department.name || "" });
    setEditModalError("");
  };

  const handleEditProgramHead = (id) => {
    const programHead = programHeads.find(item => item.id === id);
    if (!programHead) {
      setPhMsg("Program Head not found.");
      return;
    }
    const nameParts = (programHead.name || "").trim().split(/\s+/).filter(Boolean);
    setEditModal({
      type: "programHead",
      id,
      email: programHead.email || "",
      firstName: programHead.firstName || nameParts[0] || "",
      middleName: programHead.middleName || (nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""),
      lastName: programHead.lastName || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""),
      department: programHead.department || ""
    });
    setEditModalError("");
  };

  const handleEditProfessor = (id) => {
    const professor = professors.find(item => item.id === id);
    if (!professor) {
      setProfMsg("Professor not found.");
      return;
    }
    const nameParts = (professor.name || "").trim().split(/\s+/).filter(Boolean);
    setEditModal({
      type: "professor",
      id,
      email: professor.email || "",
      firstName: professor.firstName || nameParts[0] || "",
      middleName: professor.middleName || (nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : ""),
      lastName: professor.lastName || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : ""),
      departments: toNonEmptyStringArray(professor.departments).length > 0
        ? toNonEmptyStringArray(professor.departments)
        : toNonEmptyStringArray(professor.department),
      courses: toNonEmptyStringArray(professor.courses)
    });
    setEditModalError("");
  };

  const updateEditModal = (field, value) => {
    setEditModal(current => current ? { ...current, [field]: value } : current);
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!editModal || savingEdit) return;
    setSavingEdit(true);
    setEditModalError("");
    try {
      const fullName = [editModal.firstName, editModal.middleName, editModal.lastName].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
      if (editModal.type === "department") {
        const name = editModal.name.trim();
        if (!name) throw new Error("Department name is required.");
        const departmentQuery = query(
          collection(db, "departments"),
          where("school", "==", deanSchool)
        );
        const existingDepartments = await getDocs(departmentQuery);
        const duplicateExists = existingDepartments.docs.some(department =>
          department.id !== editModal.id &&
          typeof department.data().name === "string" &&
          department.data().name.trim().toLowerCase() === name.toLowerCase()
        );
        if (duplicateExists) {
          throw new Error("A department with this name already exists in your school.");
        }
        await setDoc(doc(db, "departments", editModal.id), { name }, { merge: true });
        setDeptMsg("Department updated!");
      } else if (editModal.type === "programHead") {
        if (!fullName) throw new Error("Program Head name is required.");
        await setDoc(doc(db, "users", editModal.id), {
          firstName: editModal.firstName.trim(),
          middleName: editModal.middleName.trim(),
          lastName: editModal.lastName.trim(),
          name: fullName,
          department: deanSchool === "General Education" ? "General Education" : editModal.department,
          role: "Program Head"
        }, { merge: true });
        setPhMsg("Program Head updated!");
      } else if (editModal.type === "professor") {
        if (!fullName) throw new Error("Professor name is required.");
        if (editModal.departments.length === 0) throw new Error("Please select at least one department.");
        if (editModal.courses.length === 0) throw new Error("Please select at least one course.");
        await setDoc(doc(db, "users", editModal.id), {
          firstName: editModal.firstName.trim(),
          middleName: editModal.middleName.trim(),
          lastName: editModal.lastName.trim(),
          name: fullName,
          department: editModal.departments[0],
          departments: editModal.departments,
          courses: editModal.courses,
          role: "Professor"
        }, { merge: true });
        setProfMsg("Professor updated!");
      }
      setEditModal(null);
    } catch (err) {
      console.error("Failed to update Dean-managed record:", err);
      setEditModalError(err.message || "Unable to save changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation || deletingRecord) return;
    setDeletingRecord(true);
    try {
      if (deleteConfirmation.type === "department") {
        await deleteDoc(doc(db, "departments", deleteConfirmation.id));
      } else {
        const result = await requestAccountDeletion({
          user: auth.currentUser,
          targetType: 'staff',
          documentId: deleteConfirmation.id
        });
        if (result.outcome === 'recovery_pending') {
          setPendingDeletionOperationId(result.operationId || "");
          setDeletionRecoveryMessage(`${result.error?.message || "Deletion is incomplete and needs recovery."}${result.operationId ? ` Operation ID: ${result.operationId}` : ""}`);
          return;
        }
        setPendingDeletionOperationId("");
        setDeletionRecoveryMessage("");
      }
      if (deleteConfirmation.type === "department") setDeptMsg("Department deleted!");
      if (deleteConfirmation.type === "programHead") setPhMsg("Program Head account and profile deleted.");
      if (deleteConfirmation.type === "professor") setProfMsg("Professor account and profile deleted.");
      setDeleteConfirmation(null);
    } catch (err) {
      console.error("Failed to delete Dean-managed record:", err);
      if (deleteConfirmation.type === "department") {
        setDeleteConfirmation(null);
        setDeptMsg(err.message || "Unable to delete department.");
      }
      if (deleteConfirmation.type === "programHead") setPhMsg(err.message || "Unable to delete Program Head.");
      if (deleteConfirmation.type === "professor") setProfMsg(err.message || "Unable to delete Professor.");
    } finally {
      setDeletingRecord(false);
    }
  };

  const retryPendingAccountDeletion = async () => {
    if (!pendingDeletionOperationId || deletingRecord) return;
    setDeletingRecord(true);
    try {
      const result = await requestAccountDeletion({
        user: auth.currentUser,
        operationId: pendingDeletionOperationId
      });
      if (result.outcome === 'recovery_pending') {
        setDeletionRecoveryMessage(`${result.error?.message || "Deletion is still incomplete."} Operation ID: ${pendingDeletionOperationId}`);
        return;
      }
      setPendingDeletionOperationId("");
      setDeletionRecoveryMessage("");
      if (deleteConfirmation?.type === "programHead") setPhMsg("Program Head account and profile deleted.");
      if (deleteConfirmation?.type === "professor") setProfMsg("Professor account and profile deleted.");
      setDeleteConfirmation(null);
    } catch (err) {
      setDeletionRecoveryMessage(err.message || "Deletion recovery failed.");
    } finally {
      setDeletingRecord(false);
    }
  };

  const renderManagementModals = () => (
    <>
      {editModal && (
        <div
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget && !savingEdit) setEditModal(null);
          }}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.48)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <form onSubmit={handleSaveEdit} role="dialog" aria-modal="true" aria-labelledby="dean-edit-modal-title" style={{ width: "100%", maxWidth: 620, maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 16px 48px rgba(0,0,0,0.24)" }}>
            <h2 id="dean-edit-modal-title" style={{ margin: "0 0 20px", color: "#155c2b", fontSize: 22 }}>
              Edit {editModal.type === "department" ? "Department" : editModal.type === "programHead" ? "Program Head" : "Professor"}
            </h2>
            {editModal.type === "department" ? (
              <>
                <label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Department Name</label>
                <input autoFocus value={editModal.name} onChange={event => updateEditModal("name", event.target.value)} required style={{ width: "100%", padding: 12, border: "1px solid #ccc", borderRadius: 8, boxSizing: "border-box" }} />
                <div style={{ marginTop: 14, padding: 12, background: "#f5f5f5", borderRadius: 8 }}>School: {deanSchool}</div>
              </>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
                <label style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 6 }}>
                  Email
                  <input value={editModal.email} readOnly disabled style={{ width: "100%", padding: 12, border: "1px solid #ccc", borderRadius: 8, background: "#f3f4f6", boxSizing: "border-box" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  First Name
                  <input value={editModal.firstName} onChange={event => updateEditModal("firstName", event.target.value)} required style={{ width: "100%", padding: 12, border: "1px solid #ccc", borderRadius: 8, boxSizing: "border-box" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  Middle Name
                  <input value={editModal.middleName} onChange={event => updateEditModal("middleName", event.target.value)} style={{ width: "100%", padding: 12, border: "1px solid #ccc", borderRadius: 8, boxSizing: "border-box" }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  Last Name
                  <input value={editModal.lastName} onChange={event => updateEditModal("lastName", event.target.value)} required style={{ width: "100%", padding: 12, border: "1px solid #ccc", borderRadius: 8, boxSizing: "border-box" }} />
                </label>
                <div style={{ gridColumn: "1 / -1", padding: 12, background: "#f5f5f5", borderRadius: 8 }}>School: {deanSchool}</div>
                {editModal.type === "programHead" && (
                  <label style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 6 }}>
                    Department
                    {deanSchool === "General Education" ? (
                      <input value="General Education" readOnly disabled style={{ width: "100%", padding: 12, border: "1px solid #ccc", borderRadius: 8, background: "#f3f4f6", boxSizing: "border-box" }} />
                    ) : (
                      <select value={editModal.department} onChange={event => updateEditModal("department", event.target.value)} required style={{ width: "100%", padding: 12, border: "1px solid #ccc", borderRadius: 8, background: "#fff", boxSizing: "border-box" }}>
                        <option value="">Select Department</option>
                        {departments.map(department => <option key={department.id} value={department.name}>{department.name}</option>)}
                      </select>
                    )}
                  </label>
                )}
                {editModal.type === "professor" && (
                  <>
                    <fieldset style={{ gridColumn: "1 / -1", border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
                      <legend>Departments</legend>
                      {departments.map(department => (
                        <label key={department.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <input
                            type="checkbox"
                            checked={editModal.departments.includes(department.name)}
                            onChange={() => updateEditModal("departments", editModal.departments.includes(department.name)
                              ? editModal.departments.filter(name => name !== department.name)
                              : [...editModal.departments, department.name])}
                          />
                          {department.name}
                        </label>
                      ))}
                      {editModal.departments.filter(name => !departments.some(department => department.name === name)).map(name => (
                        <label key={name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <input type="checkbox" checked onChange={() => updateEditModal("departments", editModal.departments.filter(item => item !== name))} />
                          {name} (current)
                        </label>
                      ))}
                    </fieldset>
                    <fieldset style={{ gridColumn: "1 / -1", border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
                      <legend>Subjects</legend>
                      {professorEditCourses.length === 0 && <div style={{ color: "#666" }}>No subjects found for the selected departments.</div>}
                      {professorEditCourses.map(course => {
                        const display = course.courseId ? `${course.courseId} - ${course.courseName}` : course.courseName;
                        return (
                          <label key={display} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                            <input
                              type="checkbox"
                              checked={editModal.courses.includes(display)}
                              onChange={() => updateEditModal("courses", editModal.courses.includes(display)
                                ? editModal.courses.filter(item => item !== display)
                                : [...editModal.courses, display])}
                            />
                            {display}
                          </label>
                        );
                      })}
                      {editModal.courses.filter(course => !professorEditCourses.some(option => (option.courseId ? `${option.courseId} - ${option.courseName}` : option.courseName) === course)).map(course => (
                        <label key={course} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <input type="checkbox" checked onChange={() => updateEditModal("courses", editModal.courses.filter(item => item !== course))} />
                          {course} (current)
                        </label>
                      ))}
                    </fieldset>
                  </>
                )}
              </div>
            )}
            {editModalError && <div role="alert" style={{ marginTop: 16, padding: 10, borderRadius: 6, background: "#fff0f0", color: "#a12622" }}>{editModalError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
              <button type="button" disabled={savingEdit} onClick={() => setEditModal(null)} style={{ padding: "10px 16px", border: "1px solid #ccc", borderRadius: 7, background: "#fff", cursor: savingEdit ? "not-allowed" : "pointer" }}>Cancel</button>
              <button type="submit" disabled={savingEdit} style={{ padding: "10px 18px", border: "none", borderRadius: 7, background: savingEdit ? "#78917e" : "#155c2b", color: "#fff", fontWeight: 700, cursor: savingEdit ? "not-allowed" : "pointer" }}>{savingEdit ? "Saving..." : "Save Changes"}</button>
            </div>
          </form>
        </div>
      )}
      <DeleteConfirmationModal
        open={Boolean(deleteConfirmation)}
        title={deleteConfirmation?.type === "department" ? "Delete Department?" : "Delete User?"}
        subtitle={deleteConfirmation?.type === "department" ? "Confirm department deletion" : "Confirm user deletion"}
        name={deleteConfirmation?.name}
        message={deleteConfirmation
          ? deleteConfirmation.type === "department"
            ? `Are you sure you want to delete ${deleteConfirmation.name}? This action cannot be undone.`
            : `Are you sure you want to delete ${deleteConfirmation.name}? This will remove their account, and they may lose access to EnrollEase. This action cannot be undone.`
          : ""}
        detail={deleteConfirmation?.type === "department"
          ? "Please verify the department details before proceeding."
          : "Please verify the user's details before proceeding."}
        error={deletionRecoveryMessage}
        extraContent={pendingDeletionOperationId && (
          <button type="button" onClick={retryPendingAccountDeletion} disabled={deletingRecord} style={{ marginBottom: 14 }}>
            Retry recovery
          </button>
        )}
        deleting={deletingRecord}
        confirmLabel={deleteConfirmation?.type === "department" ? "Delete Department" : "Delete User"}
        onCancel={() => setDeleteConfirmation(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );

  const profilePicUrl = "https://ui-avatars.com/api/?name=Dean";

  // Logout handler
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/academics");
    setDropdownOpen(false);
  };

  // Auth state listener - maintain session and redirect if not authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/academics");
      }
    });
    return unsubscribe;
  }, [navigate]);

  // --- Menu Page ---

  if (page === "menu") {
    return (
      <div className="dean-dashboard-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
          {(recoveryMessage || pendingRecoveryItems.length > 0) && (
            <div role="status" style={{ position: "fixed", top: 8, right: 8, zIndex: 1200, padding: 12, background: "#fff2f0", color: "#8a1c1c", borderRadius: 6 }}>
              {recoveryMessage || "A previous account deletion still needs recovery."}
              <button type="button" onClick={retryPendingDeletions} disabled={deletingRecord} style={{ marginLeft: 8 }}>
                Retry pending deletion recovery ({pendingRecoveryItems.length})
              </button>
            </div>
          )}
          {/* Sidebar */}
        <div className="dean-dashboard-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
          
          <button
            onClick={() => setPage("menu")}
            style={{
              background: page === "menu" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "menu" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Home
          </button>

          {deanSchool !== "General Education" && (
            <button
              onClick={() => setPage("department")}
              style={{
                background: page === "department" ? "#e67e22" : "transparent",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "14px 0",
                fontWeight: 600,
                fontSize: 17,
                cursor: "pointer",
                textAlign: "left",
                paddingLeft: 32,
                marginBottom: 8,
                marginRight: 12,
                textDecoration: "none",
                display: "block",
                transition: "background 0.2s, color 0.2s",
                boxShadow: page === "department" ? "0 2px 8px #e67e22aa" : "none"
              }}
            >
              Add Department
            </button>
          )}

          <button
            onClick={() => setPage("programHead")}
            style={{
              background: page === "programHead" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "programHead" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Program Head
          </button>

          <button
            onClick={() => setPage("professor")}
            style={{
              background: page === "professor" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "professor" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Professor
          </button>

          <div style={{ flex: 1 }} />
        </div>

        {/* Main area */}
        <div className="dean-dashboard-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Header */}
          <div className="dean-dashboard-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
            <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Dean Dashboard</h1>
            
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{deanName || "Dean"}</span>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={profilePicUrl}
                  alt="Profile"
                  style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }}
                />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  zIndex: 20,
                  minWidth: 200,
                  overflow: "hidden"
                }}>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                    {deanName || "Dean"}
                  </div>
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      setShowResetModal(true);
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: "transparent",
                      border: "none",
                      color: "#155c2b",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#f5fff0"}
                    onMouseLeave={(e) => e.target.style.background = "transparent"}
                  >
                    Change Password
                  </button>
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: "transparent",
                      border: "none",
                      color: "#e67e22",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                    onMouseLeave={(e) => e.target.style.background = "transparent"}
                  >
                    Log out
                  </button>
                </div>
              )}
              {showResetModal && (
                <ResetPassword email={deanEmail} onClose={() => setShowResetModal(false)} />
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="dean-dashboard-content" style={{ flex: 1, padding: 40, display: "flex", flexDirection: "column" }}>
            <section style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24, padding: 28, marginBottom: 28, background: "#155c2b", color: "#fff", borderRadius: 10 }}>
              <div>
                <div style={{ marginBottom: 8, color: "#c9e6d1", fontSize: 13, fontWeight: 700, textTransform: "uppercase" }}>Dean Dashboard</div>
                <h2 style={{ margin: "0 0 10px", fontSize: 30 }}>Welcome, {deanName || "Dean"}</h2>
                <div style={{ fontSize: 16 }}>School: {deanSchool || "Loading school..."}</div>
                <div style={{ marginTop: 8, color: "#dceee1", fontSize: 14, fontWeight: 600 }}>
                  SY / Term: {academicTermIndicator}
                </div>
              </div>
              <div style={{ minWidth: 230, padding: "16px 20px", background: "#fff", color: "#222", borderRadius: 8 }}>
                <div style={{ marginBottom: 6, color: "#666", fontSize: 13 }}>Today</div>
                <time dateTime={dashboardNow.toISOString()} style={{ fontSize: 16, fontWeight: 600 }}>
                  {dashboardNow.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </time>
                <div style={{ marginTop: 6, color: "#155c2b", fontSize: 22, fontWeight: 700 }}>
                  {dashboardNow.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
            </section>

            <h2 style={{ margin: "0 0 16px", color: "#202820", fontSize: 21 }}>School Analytics</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 32 }}>
              {[
                { label: "Total Students", value: dashboardStudentsLoading ? "..." : schoolStudents.length, detail: "Across this school", color: "#155c2b" },
                { label: "New Enrollees", value: dashboardStudentsLoading ? "..." : newEnrollees, detail: "Enrolled this month", color: "#1976d2" },
                { label: "Pending Enrollment", value: dashboardStudentsLoading ? "..." : pendingEnrollment, detail: "Awaiting enrollment approval", color: "#d97706" },
                { label: "Enrollment Growth", value: dashboardStudentsLoading ? "..." : formatEnrollmentGrowth(enrollmentGrowth), detail: "Compared with last month", color: "#008577" },
                { label: "Deans", value: dashboardDeanCount, detail: "In this school", color: "#6b7280" },
                { label: "Program Heads", value: programHeads.length, detail: "In this school", color: "#0f6caf" },
                { label: "Professors", value: professors.length, detail: "In this school", color: "#c65d17" }
              ].map(metric => (
                <article key={metric.label} style={{ minWidth: 0, padding: "18px 20px", background: "#fff", border: "1px solid #dedede", borderTop: `4px solid ${metric.color}`, borderRadius: 8 }}>
                  <div style={{ marginBottom: 12, color: "#555", fontSize: 14, fontWeight: 600 }}>{metric.label}</div>
                  <div style={{ color: metric.color, fontSize: typeof metric.value === "string" && metric.value.length > 8 ? 18 : 30, fontWeight: 700, lineHeight: 1.1, overflowWrap: "anywhere" }}>{metric.value}</div>
                  <div style={{ marginTop: 8, color: "#777", fontSize: 12 }}>{metric.detail}</div>
                </article>
              ))}
            </div>

            <section>
              <h2 style={{ margin: "0 0 14px", color: "#202820", fontSize: 21 }}>Enrollment by Department</h2>
              <div style={{ overflowX: "auto", background: "#fff", border: "1px solid #ccc", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f5f7f5" }}>
                      <th style={{ padding: 12, borderBottom: "1px solid #ccc", textAlign: "left" }}>Department</th>
                      <th style={{ padding: 12, borderBottom: "1px solid #ccc", textAlign: "right" }}>Total Students</th>
                      <th style={{ padding: 12, borderBottom: "1px solid #ccc", textAlign: "right" }}>New Enrollees</th>
                      <th style={{ padding: 12, borderBottom: "1px solid #ccc", textAlign: "right" }}>Pending Enrollment</th>
                      <th style={{ padding: 12, borderBottom: "1px solid #ccc", textAlign: "right" }}>Enrollment Growth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departmentEnrollmentStats.length > 0 ? departmentEnrollmentStats.map(stats => (
                      <tr key={stats.department}>
                        <td style={{ padding: 12, borderBottom: "1px solid #e5e5e5", fontWeight: 600 }}>{stats.department}</td>
                        <td style={{ padding: 12, borderBottom: "1px solid #e5e5e5", textAlign: "right" }}>{dashboardStudentsLoading ? "..." : stats.totalStudents}</td>
                        <td style={{ padding: 12, borderBottom: "1px solid #e5e5e5", textAlign: "right" }}>{dashboardStudentsLoading ? "..." : stats.newEnrollees}</td>
                        <td style={{ padding: 12, borderBottom: "1px solid #e5e5e5", textAlign: "right" }}>{dashboardStudentsLoading ? "..." : stats.pendingEnrollment}</td>
                        <td style={{ padding: 12, borderBottom: "1px solid #e5e5e5", textAlign: "right", fontWeight: 600, fontSize: stats.enrollmentGrowth === null ? 12 : 16 }}>{dashboardStudentsLoading ? "..." : formatEnrollmentGrowth(stats.enrollmentGrowth)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} style={{ padding: 18, color: "#777", textAlign: "center" }}>
                          {deanSchool ? "No departments found for this school." : "Loading school departments..."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  // --- Add Department Page ---
  if (page === "department") {
    if (deanSchool === "General Education") {
      setPage("menu");
      return null;
    }
    return (
      <div className="dean-dashboard-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
        {/* Sidebar */}
        <div className="dean-dashboard-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
          
          <button
            onClick={() => setPage("menu")}
            style={{
              background: page === "menu" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "menu" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Home
          </button>

          {deanSchool !== "General Education" && (
            <button
              onClick={() => setPage("department")}
              style={{
                background: page === "department" ? "#e67e22" : "transparent",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "14px 0",
                fontWeight: 600,
                fontSize: 17,
                cursor: "pointer",
                textAlign: "left",
                paddingLeft: 32,
                marginBottom: 8,
                marginRight: 12,
                textDecoration: "none",
                display: "block",
                transition: "background 0.2s, color 0.2s",
                boxShadow: page === "department" ? "0 2px 8px #e67e22aa" : "none"
              }}
            >
              Add Department
            </button>
          )}

          <button
            onClick={() => setPage("programHead")}
            style={{
              background: page === "programHead" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "programHead" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Program Head
          </button>

          <button
            onClick={() => setPage("professor")}
            style={{
              background: page === "professor" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "professor" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Professor
          </button>

          <div style={{ flex: 1 }} />
        </div>

        {/* Main area */}
        <div className="dean-dashboard-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Header */}
          <div className="dean-dashboard-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
            <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Add Department</h1>
            
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{deanName || "Dean"}</span>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={profilePicUrl}
                  alt="Profile"
                  style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }}
                />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  zIndex: 20,
                  minWidth: 200,
                  overflow: "hidden"
                }}>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                    {deanName || "Dean"}
                  </div>
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: "transparent",
                      border: "none",
                      color: "#e67e22",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                    onMouseLeave={(e) => e.target.style.background = "transparent"}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="dean-dashboard-content" style={{ flex: 1, padding: 40, display: "flex", flexDirection: "column", overflowY: "auto" }}>
            
            <form onSubmit={handleAddDepartment} style={{ marginBottom: 24, width: "100%", maxWidth: 900, margin: "0 auto 24px" }}>
              <div style={{ padding: 20, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <h2 style={{ margin: "0 0 20px", color: "#155c2b", fontSize: 20 }}>Add Department</h2>
                <div className="dean-professor-fields" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
                  <div>
                    <label htmlFor="dean-department-name" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Department name</label>
                    <input
                      id="dean-department-name"
                      type="text"
                      value={departmentName}
                      onChange={e => {
                        setDepartmentName(e.target.value);
                        if (deptMsg) setDeptMsg("");
                      }}
                      required
                      disabled={deptSaving}
                      style={{ width: "100%", minWidth: 0, minHeight: 48, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="dean-department-school" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>School</label>
                    <div id="dean-department-school" style={{ width: "100%", minHeight: 48, padding: "14px 16px", background: "#f5f5f5", border: "1px solid #ccc", borderRadius: 10, color: "#333", fontWeight: 700 }}>
                      {deanSchool}
                    </div>
                  </div>
                </div>
                {deptMsg && (
                  <div role="status" aria-live="polite" style={{ marginTop: 16, padding: 12, borderRadius: 8, border: `1px solid ${/added|updated|deleted/i.test(deptMsg) ? "#b7dfc2" : "#f1c3c3"}`, background: /added|updated|deleted/i.test(deptMsg) ? "#f0faf2" : "#fff6f6", color: /added|updated|deleted/i.test(deptMsg) ? "#155c2b" : "#a12626" }}>
                    {deptMsg}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: "1px solid #eee" }}>
                  <button type="submit" disabled={deptSaving} style={{ minWidth: 210, padding: "14px 20px", background: deptSaving ? "#78917e" : "#155c2b", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: deptSaving ? "wait" : "pointer" }}>
                    {deptSaving ? "SAVING..." : "ADD DEPARTMENT"}
                  </button>
                </div>
              </div>
            </form>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 40, marginBottom: 12, padding: 12, background: "#f9f9f9", border: "1px solid #ccc", borderRadius: "8px 8px 0 0" }}>
              <select
                aria-label="Filter departments by name"
                value={deptNameFilter}
                onChange={e => {
                  setDeptNameFilter(e.target.value);
                  setDeptTablePage(1);
                }}
                style={{ flex: "1 1 260px", minWidth: 0, padding: "12px 14px", border: "1px solid #ccc", borderRadius: 8, background: "#fff" }}
              >
                <option value="">All Departments</option>
                {departmentFilterOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  setDeptNameFilter("");
                  setDeptTablePage(1);
                }}
                disabled={!deptNameFilter}
                style={{ padding: "12px 16px", border: "1px solid #155c2b", borderRadius: 8, background: deptNameFilter ? "#fff" : "#f0f0f0", color: deptNameFilter ? "#155c2b" : "#888", fontWeight: 600, cursor: deptNameFilter ? "pointer" : "not-allowed" }}
              >
                Clear Filters
              </button>
            </div>
            <div className="dean-dashboard-table-container" style={{ overflowX: "auto", marginTop: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ccc" }}>
                <thead>
                  <tr style={{ background: "#f9f9f9" }}>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Department Name</th>
                    <th style={{ border: "1px solid #ccc", padding: 8, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);
                    const paginated = filteredDepartments.slice((deptTablePage - 1) * itemsPerPage, deptTablePage * itemsPerPage);
                    return paginated.length > 0 ? paginated.map(dept => (
                      <tr key={dept.id}>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{dept.name}</td>
                        <td style={{ border: "1px solid #ccc", padding: 8, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                          <button
                            onClick={() => handleEditDepartment(dept.id)}
                            style={{ padding: "8px 14px", background: "#0f6caf", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "background 0.2s" }}
                            onMouseEnter={e => e.target.style.background = "#0c5487"}
                            onMouseLeave={e => e.target.style.background = "#0f6caf"}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteDepartment(dept.id)}
                            style={{ padding: "8px 14px", background: "#dc3545", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "background 0.2s" }}
                            onMouseEnter={e => e.target.style.background = "#b52a3a"}
                            onMouseLeave={e => e.target.style.background = "#dc3545"}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={2} style={{ textAlign: "center", color: "#888", border: "1px solid #ccc", padding: 8 }}>
                          {deptNameFilter ? "No departments match this filter." : "No departments added yet."}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {(() => {
              const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);
              return totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
                  <button onClick={() => setDeptTablePage(deptTablePage - 1)} disabled={deptTablePage === 1} style={{ padding: '6px 14px', borderRadius: 4 }}>Prev</button>
                  <span style={{ fontWeight: 500, fontSize: 16 }}>Page {deptTablePage} of {totalPages}</span>
                  <button onClick={() => setDeptTablePage(deptTablePage + 1)} disabled={deptTablePage === totalPages} style={{ padding: '6px 14px', borderRadius: 4 }}>Next</button>
                </div>
              );
            })()}
            {renderManagementModals()}
          </div>
        </div>
      </div>
    );
  }

  // --- Add Program Head Page ---
  if (page === "programHead") {
    return (
      <div className="dean-dashboard-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
        {/* Sidebar */}
        <div className="dean-dashboard-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
          
          <button
            onClick={() => setPage("menu")}
            style={{
              background: page === "menu" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "menu" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Home
          </button>

          {deanSchool !== "General Education" && (
            <button
              onClick={() => setPage("department")}
              style={{
                background: page === "department" ? "#e67e22" : "transparent",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "14px 0",
                fontWeight: 600,
                fontSize: 17,
                cursor: "pointer",
                textAlign: "left",
                paddingLeft: 32,
                marginBottom: 8,
                marginRight: 12,
                textDecoration: "none",
                display: "block",
                transition: "background 0.2s, color 0.2s",
                boxShadow: page === "department" ? "0 2px 8px #e67e22aa" : "none"
              }}
            >
              Add Department
            </button>
          )}

          <button
            onClick={() => setPage("programHead")}
            style={{
              background: page === "programHead" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "programHead" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Program Head
          </button>

          <button
            onClick={() => setPage("professor")}
            style={{
              background: page === "professor" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "professor" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Professor
          </button>

          <div style={{ flex: 1 }} />
        </div>

        {/* Main area */}
        <div className="dean-dashboard-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Header */}
          <div className="dean-dashboard-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
            <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Add Program Head</h1>
            
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{deanName || "Dean"}</span>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={profilePicUrl}
                  alt="Profile"
                  style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }}
                />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  zIndex: 20,
                  minWidth: 200,
                  overflow: "hidden"
                }}>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                    {deanName || "Dean"}
                  </div>
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: "transparent",
                      border: "none",
                      color: "#e67e22",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                    onMouseLeave={(e) => e.target.style.background = "transparent"}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="dean-dashboard-content" style={{ flex: 1, padding: 40, display: "flex", flexDirection: "column", overflowY: "auto" }}>
          
            <form onSubmit={handleAddProgramHead} style={{ marginBottom: 24, display: "flex", flexDirection: "column", gap: 20, width: "100%", maxWidth: 900, margin: "0 auto 24px" }}>
              <div style={{ padding: 20, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <div style={{ marginBottom: 20 }}>
                  <h2 style={{ margin: "0 0 6px", color: "#155c2b", fontSize: 20 }}>Add Program Head</h2>
                </div>
                <div className="dean-professor-fields" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="program-head-school" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>School</label>
                    <div id="program-head-school" style={{ width: "100%", minHeight: 48, padding: "14px 16px", background: "#f5f5f5", border: "1px solid #ccc", borderRadius: 10, color: "#333", fontWeight: 700 }}>
                      {deanSchool}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="program-head-first-name" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>First name</label>
                    <input
                      id="program-head-first-name"
                      type="text"
                      value={phFirstName}
                      onChange={e => setPhFirstName(e.target.value)}
                      required
                      disabled={phSaving || Boolean(phPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="program-head-middle-name" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Middle name <span style={{ color: "#666", fontWeight: 400 }}>(optional)</span></label>
                    <input
                      id="program-head-middle-name"
                      type="text"
                      value={phMiddleName}
                      onChange={e => setPhMiddleName(e.target.value)}
                      disabled={phSaving || Boolean(phPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="program-head-last-name" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Last name</label>
                    <input
                      id="program-head-last-name"
                      type="text"
                      value={phLastName}
                      onChange={e => setPhLastName(e.target.value)}
                      required
                      disabled={phSaving || Boolean(phPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="program-head-email" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Email address</label>
                    <input
                      id="program-head-email"
                      type="email"
                      value={phEmail}
                      onChange={e => setPhEmail(e.target.value)}
                      required
                      disabled={phSaving || Boolean(phPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="program-head-password" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Generated password</label>
                    <div style={{ display: "flex", alignItems: "stretch", gap: 8, flexWrap: "wrap" }}>
                      <input
                        id="program-head-password"
                        type={phShowPassword ? "text" : "password"}
                        value={phPassword}
                        readOnly
                        required
                        aria-label="Generated Program Head password"
                        style={{ flex: "1 1 260px", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#f9f9f9", fontFamily: "monospace", letterSpacing: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setPhShowPassword(visible => !visible)}
                        aria-label={phShowPassword ? "Hide password" : "Show password"}
                        aria-pressed={phShowPassword}
                        style={{ minWidth: 78, padding: "10px 14px", borderRadius: 8, border: "1px solid #dfe7e3", background: "#fff", color: "#155c2b", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        {phShowPassword ? "Hide" : "Show"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPhPassword(generateProgramHeadPassword())}
                        disabled={phSaving || Boolean(phPendingCreation)}
                        style={{ minWidth: 112, padding: "10px 14px", borderRadius: 8, border: "1px solid #155c2b", background: "#fff", color: "#155c2b", fontWeight: 600, cursor: phSaving || phPendingCreation ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                      >
                        Generate new
                      </button>
                    </div>
                    <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>A password will be generated automatically and sent to the user's email.</p>
                  </div>
                  <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
                    <label id="program-head-department-label" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Department</label>
                    {deanSchool === "General Education" ? (
                      <select id="program-head-department" value="General Education" disabled style={{ width: "100%", minHeight: 48, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#f0f0f0" }}>
                        <option value="General Education">General Education</option>
                      </select>
                    ) : (
                      <>
                        <button
                          id="program-head-department"
                          type="button"
                          aria-labelledby="program-head-department-label"
                          aria-expanded={phDepartmentDropdownOpen}
                          onClick={() => setPhDepartmentDropdownOpen(open => !open)}
                          disabled={phSaving || Boolean(phPendingCreation)}
                          style={{ width: "100%", minHeight: 48, minWidth: 0, padding: 14, textAlign: "left", borderRadius: 10, border: "1px solid #ccc", background: "#fff", cursor: phSaving || phPendingCreation ? "not-allowed" : "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
                        >
                          <span>{phDepartment || "Select department"}</span>
                          <span style={{ flexShrink: 0, transform: phDepartmentDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>⌄</span>
                        </button>
                        {phDepartmentDropdownOpen && (
                          <div style={{ width: "100%", maxHeight: 220, overflowY: "auto", marginTop: 8, background: "#fff", border: "1px solid #ccc", borderRadius: 10, padding: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                            {departments.length === 0 ? (
                              <div style={{ padding: 8, color: "#777" }}>No departments available</div>
                            ) : departments.map(dept => (
                              <button
                                key={dept.id}
                                type="button"
                                onClick={() => {
                                  setPhDepartment(dept.name);
                                  setPhDepartmentDropdownOpen(false);
                                }}
                                disabled={phSaving || Boolean(phPendingCreation)}
                                style={{ display: "block", width: "100%", padding: "10px 12px", textAlign: "left", border: "none", borderRadius: 6, background: phDepartment === dept.name ? "#edf7ef" : "#fff", color: "#333", cursor: "pointer" }}
                              >
                                {dept.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {phMsg && (
                  <div role="status" aria-live="polite" style={{ marginTop: 16, padding: 12, borderRadius: 8, border: `1px solid ${/added|updated|deleted/i.test(phMsg) ? "#b7dfc2" : "#f1c3c3"}`, background: /added|updated|deleted/i.test(phMsg) ? "#f0faf2" : "#fff6f6", color: /added|updated|deleted/i.test(phMsg) ? "#155c2b" : "#a12626" }}>
                    {phMsg}
                  </div>
                )}
                {phSaving && (
                  <div role="status" aria-live="polite" style={{ marginTop: 16, color: "#555", fontSize: 14 }}>
                    {phSaveStage}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: "1px solid #eee" }}>
                  <button type="submit" disabled={phSaving} style={{ minWidth: 210, padding: "14px 20px", background: phSaving ? "#78917e" : "#155c2b", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: phSaving ? "wait" : "pointer", transition: "background 0.2s" }}>
                    {phSaving ? phSaveStage : phPendingCreation ? "RETRY SETUP" : "ADD PROGRAM HEAD"}
                  </button>
                </div>
              </div>
            </form>
            <div className="dean-dashboard-table-container" style={{ overflowX: "auto", marginTop: 0 }}>
              <div className="dean-professor-filters" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", alignItems: "center", gap: 12, width: "100%", padding: 12, background: "#f9f9f9", border: "1px solid #ccc", borderBottom: "none", boxSizing: "border-box" }}>
                <input
                  type="search"
                  placeholder="Search by name"
                  aria-label="Search program heads by name"
                  value={phNameFilter}
                  onChange={e => {
                    setPhNameFilter(e.target.value);
                    setPhTablePage(1);
                  }}
                  style={{ width: "100%", minWidth: 0, padding: "12px 14px", border: "1px solid #ccc", borderRadius: 8, background: "#fff" }}
                />
                <select
                  aria-label="Filter program heads by department"
                  value={phDepartmentFilter}
                  onChange={e => {
                    setPhDepartmentFilter(e.target.value);
                    setPhTablePage(1);
                  }}
                  style={{ width: "100%", minWidth: 0, padding: "12px 14px", border: "1px solid #ccc", borderRadius: 8, background: "#fff" }}
                >
                  <option value="">All Departments</option>
                  {programHeadDepartmentOptions.map(department => (
                    <option key={department} value={department}>{department}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setPhNameFilter("");
                    setPhDepartmentFilter("");
                    setPhTablePage(1);
                  }}
                  disabled={!phNameFilter && !phDepartmentFilter}
                  style={{ padding: "12px 16px", border: "1px solid #155c2b", borderRadius: 8, background: phNameFilter || phDepartmentFilter ? "#fff" : "#f0f0f0", color: phNameFilter || phDepartmentFilter ? "#155c2b" : "#888", fontWeight: 600, cursor: phNameFilter || phDepartmentFilter ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}
                >
                  Clear Filters
                </button>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ccc" }}>
                <thead>
                  <tr style={{ background: "#f9f9f9" }}>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Name</th>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Email</th>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Department</th>
                    <th style={{ border: "1px solid #ccc", padding: 8, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const totalPages = Math.ceil(filteredProgramHeads.length / itemsPerPage);
                    const paginated = filteredProgramHeads.slice((phTablePage - 1) * itemsPerPage, phTablePage * itemsPerPage);
                    return paginated.length > 0 ? paginated.map(ph => (
                      <tr key={ph.id}>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{ph.name}</td>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{ph.email}</td>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{ph.department}</td>
                        <td style={{ border: "1px solid #ccc", padding: 8, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                          <button
                            onClick={() => handleEditProgramHead(ph.id)}
                            style={{ padding: "8px 14px", background: "#0f6caf", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "background 0.2s" }}
                            onMouseEnter={e => e.target.style.background = "#0c5487"}
                            onMouseLeave={e => e.target.style.background = "#0f6caf"}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProgramHead(ph.id)}
                            style={{ padding: "8px 14px", background: "#dc3545", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "background 0.2s" }}
                            onMouseEnter={e => e.target.style.background = "#b52a3a"}
                            onMouseLeave={e => e.target.style.background = "#dc3545"}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", color: "#888", border: "1px solid #ccc", padding: 8 }}>
                          {programHeads.length === 0 ? "No program heads added yet." : "No program heads match the selected filters."}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {(() => {
              const totalPages = Math.ceil(filteredProgramHeads.length / itemsPerPage);
              return totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
                  <button onClick={() => setPhTablePage(phTablePage - 1)} disabled={phTablePage === 1} style={{ padding: '6px 14px', borderRadius: 4 }}>Prev</button>
                  <span style={{ fontWeight: 500, fontSize: 16 }}>Page {phTablePage} of {totalPages}</span>
                  <button onClick={() => setPhTablePage(phTablePage + 1)} disabled={phTablePage === totalPages} style={{ padding: '6px 14px', borderRadius: 4 }}>Next</button>
                </div>
              );
            })()}
            {renderManagementModals()}
          </div>
        </div>
      </div>
    );
  }

  // --- Add Professor Page ---
  if (page === "professor") {
    return (
      <div className="dean-dashboard-layout" style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
        {/* Sidebar */}
        <div className="dean-dashboard-sidebar" style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
          
          <button
            onClick={() => setPage("menu")}
            style={{
              background: page === "menu" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "menu" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Home
          </button>

          {deanSchool !== "General Education" && (
            <button
              onClick={() => setPage("department")}
              style={{
                background: page === "department" ? "#e67e22" : "transparent",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "14px 0",
                fontWeight: 600,
                fontSize: 17,
                cursor: "pointer",
                textAlign: "left",
                paddingLeft: 32,
                marginBottom: 8,
                marginRight: 12,
                textDecoration: "none",
                display: "block",
                transition: "background 0.2s, color 0.2s",
                boxShadow: page === "department" ? "0 2px 8px #e67e22aa" : "none"
              }}
            >
              Add Department
            </button>
          )}

          <button
            onClick={() => setPage("programHead")}
            style={{
              background: page === "programHead" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "programHead" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Program Head
          </button>

          <button
            onClick={() => setPage("professor")}
            style={{
              background: page === "professor" ? "#e67e22" : "transparent",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "14px 0",
              fontWeight: 600,
              fontSize: 17,
              cursor: "pointer",
              textAlign: "left",
              paddingLeft: 32,
              marginBottom: 8,
              marginRight: 12,
              textDecoration: "none",
              display: "block",
              transition: "background 0.2s, color 0.2s",
              boxShadow: page === "professor" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Add Professor
          </button>

          <div style={{ flex: 1 }} />
        </div>

        {/* Main area */}
        <div className="dean-dashboard-main" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {/* Header */}
          <div className="dean-dashboard-header" style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "space-between" }}>
            <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Add Professor</h1>
            
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{deanName || "Dean"}</span>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={profilePicUrl}
                  alt="Profile"
                  style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }}
                />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  zIndex: 20,
                  minWidth: 200,
                  overflow: "hidden"
                }}>
                  <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                    {deanName || "Dean"}
                  </div>
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      background: "transparent",
                      border: "none",
                      color: "#e67e22",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                    onMouseLeave={(e) => e.target.style.background = "transparent"}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="dean-dashboard-content" style={{ flex: 1, padding: 40, display: "flex", flexDirection: "column", overflowY: "auto" }}>

            {profMsg && (() => {
              const isSuccess = /added|updated|deleted/i.test(profMsg);
              return (
                <div
                  role={isSuccess ? "status" : "alert"}
                  style={{
                    position: "relative",
                    alignSelf: "flex-end",
                    width: "fit-content",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    maxWidth: "min(420px, calc(100vw - 48px))",
                    marginBottom: 16,
                    padding: "14px 16px",
                    background: isSuccess ? "#edf7ef" : "#fff0f0",
                    color: isSuccess ? "#155c2b" : "#a12622",
                    border: `1px solid ${isSuccess ? "#b8d9bf" : "#e7b8b6"}`,
                    borderLeft: `4px solid ${isSuccess ? "#218838" : "#c62828"}`,
                    borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                    fontWeight: 600
                  }}
                >
                  <span style={{ flex: 1 }}>{profMsg}</span>
                  <button
                    type="button"
                    onClick={() => setProfMsg("")}
                    aria-label="Dismiss notification"
                    style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 18, fontWeight: 700, lineHeight: 1, padding: 2 }}
                  >
                    x
                  </button>
                </div>
              );
            })()}

            <form onSubmit={handleAddProfessor} style={{ marginBottom: 48, display: "flex", flexDirection: "column", gap: 20, width: "100%", maxWidth: 900, margin: "0 auto" }}>
              <div style={{ padding: 20, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <div style={{ marginBottom: 20 }}>
                  <h2 style={{ margin: "0 0 6px", color: "#155c2b", fontSize: 20 }}>Add Professor</h2>
                </div>
                <div className="dean-professor-fields" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="professor-school" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>School</label>
                    <div id="professor-school" style={{ width: "100%", minHeight: 48, padding: "14px 16px", background: "#f5f5f5", border: "1px solid #ccc", borderRadius: 10, color: "#333", fontWeight: 700 }}>
                      {deanSchool}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="professor-first-name" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>First name</label>
                    <input
                      id="professor-first-name"
                      type="text"
                      value={profFirstName}
                      onChange={e => setProfFirstName(e.target.value)}
                      required
                      disabled={profSaving || Boolean(profPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="professor-middle-name" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Middle name <span style={{ color: "#666", fontWeight: 400 }}>(optional)</span></label>
                    <input
                      id="professor-middle-name"
                      type="text"
                      value={profMiddleName}
                      onChange={e => setProfMiddleName(e.target.value)}
                      disabled={profSaving || Boolean(profPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="professor-last-name" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Last name</label>
                    <input
                      id="professor-last-name"
                      type="text"
                      value={profLastName}
                      onChange={e => setProfLastName(e.target.value)}
                      required
                      disabled={profSaving || Boolean(profPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="professor-email" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Email address</label>
                    <input
                      id="professor-email"
                      type="email"
                      value={profEmail}
                      onChange={e => setProfEmail(e.target.value)}
                      required
                      disabled={profSaving || Boolean(profPendingCreation)}
                      style={{ width: "100%", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#fff" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="professor-password" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Generated password</label>
                    <div style={{ display: "flex", alignItems: "stretch", gap: 8, flexWrap: "wrap" }}>
                      <input
                        id="professor-password"
                        type={profShowPassword ? "text" : "password"}
                        value={profPassword}
                        readOnly
                        required
                        aria-label="Generated Professor password"
                        style={{ flex: "1 1 260px", minWidth: 0, padding: 14, borderRadius: 10, border: "1px solid #ccc", background: "#f9f9f9", fontFamily: "monospace", letterSpacing: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setProfShowPassword(visible => !visible)}
                        aria-label={profShowPassword ? "Hide password" : "Show password"}
                        aria-pressed={profShowPassword}
                        style={{ minWidth: 78, padding: "10px 14px", borderRadius: 8, border: "1px solid #dfe7e3", background: "#fff", color: "#155c2b", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        {profShowPassword ? "Hide" : "Show"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setProfPassword(generateProgramHeadPassword())}
                        disabled={profSaving || Boolean(profPendingCreation)}
                        style={{ minWidth: 112, padding: "10px 14px", borderRadius: 8, border: "1px solid #155c2b", background: "#fff", color: "#155c2b", fontWeight: 600, cursor: profSaving || profPendingCreation ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                      >
                        Generate new
                      </button>
                    </div>
                    <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>A password will be generated automatically and sent to the user's email.</p>
                  </div>
                  <div style={{ position: "relative", minWidth: 0 }}>
                    <label id="professor-departments-label" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Department(s)</label>
                    <button
                      type="button"
                      aria-labelledby="professor-departments-label"
                      aria-expanded={departmentDropdownOpen}
                      onClick={() => setDepartmentDropdownOpen(open => !open)}
                      disabled={profSaving || Boolean(profPendingCreation)}
                      style={{ width: "100%", minHeight: 48, minWidth: 0, padding: 14, textAlign: "left", borderRadius: 10, border: "1px solid #ccc", background: "#fff", cursor: profSaving || profPendingCreation ? "not-allowed" : "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profDepartments.length > 0 ? profDepartments.join(", ") : "Select departments"}</span>
                      <span style={{ flexShrink: 0, transform: departmentDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>⌄</span>
                    </button>
                    {departmentDropdownOpen && (
                      <div style={{ position: "static", width: "100%", maxHeight: 220, overflowY: "auto", marginTop: 8, background: "#fff", border: "1px solid #ccc", borderRadius: 10, padding: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                        {departments.map(dept => (
                          <label key={dept.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, cursor: profSaving || profPendingCreation ? "not-allowed" : "pointer" }}>
                            <input
                              type="checkbox"
                              checked={profDepartments.includes(dept.name)}
                              onChange={() => toggleProfDepartment(dept.name)}
                              disabled={profSaving || Boolean(profPendingCreation)}
                            />
                            <span>{dept.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ position: "relative", minWidth: 0 }}>
                    <label id="professor-courses-label" style={{ display: "block", marginBottom: 6, color: "#333", fontWeight: 600 }}>Subject(s)</label>
                    <button
                      type="button"
                      aria-labelledby="professor-courses-label"
                      aria-expanded={coursesDropdownOpen}
                      onClick={() => setCoursesDropdownOpen(open => !open)}
                      disabled={profSaving || Boolean(profPendingCreation)}
                      style={{ width: "100%", minHeight: 48, minWidth: 0, padding: 14, textAlign: "left", borderRadius: 10, border: "1px solid #ccc", background: "#fff", cursor: profSaving || profPendingCreation ? "not-allowed" : "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profCourses.length > 0 ? profCourses.join(", ") : "Select subjects"}</span>
                      <span style={{ flexShrink: 0, transform: coursesDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>⌄</span>
                    </button>
                    {coursesDropdownOpen && (
                      <div style={{ position: "static", width: "100%", maxHeight: 240, overflowY: "auto", marginTop: 8, background: "#fff", border: "1px solid #ccc", borderRadius: 10, padding: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                        {courseList.length === 0 ? (
                          <div style={{ color: "#777", padding: 8 }}>No courses found</div>
                        ) : (
                          courseList.map((course, idx) => {
                            const courseDisplay = course.courseId
                              ? `${course.courseId} - ${course.courseName}`
                              : course.courseName;
                            return (
                              <label key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: profSaving || profPendingCreation ? "not-allowed" : "pointer" }}>
                                <input
                                  type="checkbox"
                                  checked={profCourses.includes(courseDisplay)}
                                  onChange={() => toggleProfCourse(courseDisplay)}
                                  disabled={profSaving || Boolean(profPendingCreation)}
                                />
                                <span>{courseDisplay}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {profSaving && (
                  <div role="status" aria-live="polite" style={{ marginTop: 16, color: "#555", fontSize: 14 }}>
                    {profSaveStage}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: "1px solid #eee" }}>
                  <button type="submit" disabled={profSaving} style={{ minWidth: 210, padding: "14px 20px", background: profSaving ? "#78917e" : "#155c2b", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: profSaving ? "wait" : "pointer" }}>
                    {profSaving ? profSaveStage : profPendingCreation ? "RETRY SETUP" : "ADD PROFESSOR"}
                  </button>
                </div>
              </div>
            </form>
            <div className="dean-dashboard-table-container" style={{ overflowX: "auto", marginTop: 24 }}>
            <div className="dean-professor-filters" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", alignItems: "start", gap: 12, width: "100%", padding: 12, background: "#f9f9f9", border: "1px solid #ccc", borderBottom: "none", boxSizing: "border-box" }}>
              <input
                type="search"
                placeholder="Search by name"
                aria-label="Search professors by name"
                value={profNameFilter}
                onChange={e => {
                  setProfNameFilter(e.target.value);
                  setProfTablePage(1);
                }}
                style={{ width: "100%", minWidth: 0, padding: "12px 14px", border: "1px solid #ccc", borderRadius: 8, background: "#fff" }}
              />
              <select
                aria-label="Filter professors by department"
                value={profDepartmentFilter}
                onChange={e => {
                  setProfDepartmentFilter(e.target.value);
                  setProfTablePage(1);
                }}
                style={{ width: "100%", minWidth: 0, padding: "12px 14px", border: "1px solid #ccc", borderRadius: 8, background: "#fff" }}
              >
                <option value="">All Departments</option>
                {professorDepartmentOptions.map(department => (
                  <option key={department} value={department}>{department}</option>
                ))}
              </select>
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                <button
                  type="button"
                  aria-label="Filter professors by subject"
                  aria-haspopup="listbox"
                  aria-expanded={profSubjectDropdownOpen}
                  onClick={() => setProfSubjectDropdownOpen(open => !open)}
                  style={{ width: "100%", minWidth: 0, padding: "12px 14px", border: "1px solid #ccc", borderRadius: 8, background: "#fff", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, textAlign: "left" }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profSubjectFilter || "All Subjects"}</span>
                  <span aria-hidden="true">{profSubjectDropdownOpen ? "⌃" : "⌄"}</span>
                </button>
                {profSubjectDropdownOpen && (
                  <div role="listbox" aria-label="Subject filter options" style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #ccc", borderRadius: 8, background: "#fff" }}>
                    {["", ...professorSubjectOptions].map(subject => (
                      <button
                        key={subject || "all-subjects"}
                        type="button"
                        role="option"
                        aria-selected={profSubjectFilter === subject}
                        onClick={() => {
                          setProfSubjectFilter(subject);
                          setProfTablePage(1);
                          setProfSubjectDropdownOpen(false);
                        }}
                        style={{ display: "block", width: "100%", padding: "8px 10px", border: "none", background: profSubjectFilter === subject ? "#e8f1eb" : "#fff", color: "#222", textAlign: "left", cursor: "pointer" }}
                      >
                        {subject || "All Subjects"}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setProfNameFilter("");
                  setProfDepartmentFilter("");
                  setProfSubjectFilter("");
                  setProfSubjectDropdownOpen(false);
                  setProfTablePage(1);
                }}
                disabled={!profNameFilter && !profDepartmentFilter && !profSubjectFilter}
                style={{ padding: "12px 16px", border: "1px solid #155c2b", borderRadius: 8, background: profNameFilter || profDepartmentFilter || profSubjectFilter ? "#fff" : "#f0f0f0", color: profNameFilter || profDepartmentFilter || profSubjectFilter ? "#155c2b" : "#888", fontWeight: 600, cursor: profNameFilter || profDepartmentFilter || profSubjectFilter ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}
              >
                Clear Filters
              </button>
            </div>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ccc" }}>
                <thead>
                  <tr style={{ background: "#f9f9f9" }}>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Name</th>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Email</th>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Department</th>
                    <th style={{ border: "1px solid #ccc", padding: 8 }}>Subject</th>
                    <th style={{ border: "1px solid #ccc", padding: 8, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const totalPages = Math.ceil(filteredProfessors.length / itemsPerPage);
                    const paginated = filteredProfessors.slice((profTablePage - 1) * itemsPerPage, profTablePage * itemsPerPage);
                    return paginated.length > 0 ? paginated.map(prof => (
                      <tr key={prof.id}>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{prof.name}</td>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>{prof.email}</td>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>
                          {Array.isArray(prof.department)
                            ? prof.department.join(", ")
                            : prof.department}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: 8 }}>
                          {Array.isArray(prof.courses) ? (
                            prof.courses.map((c, i) => (
                              <div key={i} style={{ whiteSpace: 'pre-wrap' }}>- {c}</div>
                            ))
                          ) : (
                            prof.courses ? <div>- {prof.courses}</div> : null
                          )}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: 8, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                          <button
                            onClick={() => handleEditProfessor(prof.id)}
                            style={{ padding: "8px 14px", background: "#0f6caf", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "background 0.2s" }}
                            onMouseEnter={e => e.target.style.background = "#0c5487"}
                            onMouseLeave={e => e.target.style.background = "#0f6caf"}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProfessor(prof.id)}
                            style={{ padding: "8px 14px", background: "#dc3545", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, transition: "background 0.2s" }}
                            onMouseEnter={e => e.target.style.background = "#b52a3a"}
                            onMouseLeave={e => e.target.style.background = "#dc3545"}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "#888", border: "1px solid #ccc", padding: 8 }}>
                          {professors.length === 0 ? "No professors added yet." : "No professors match the selected filters."}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {(() => {
              const totalPages = Math.ceil(filteredProfessors.length / itemsPerPage);
              return totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
                  <button onClick={() => setProfTablePage(profTablePage - 1)} disabled={profTablePage === 1} style={{ padding: '6px 14px', borderRadius: 4 }}>Prev</button>
                  <span style={{ fontWeight: 500, fontSize: 16 }}>Page {profTablePage} of {totalPages}</span>
                  <button onClick={() => setProfTablePage(profTablePage + 1)} disabled={profTablePage === totalPages} style={{ padding: '6px 14px', borderRadius: 4 }}>Next</button>
                </div>
              );
            })()}
            {renderManagementModals()}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default DeanDashboard;